import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ProximityToggle } from "@/components/ProximityToggle";
import { SocialConsent, SOCIAL_MSG, MessagesConsent } from "@/components/SocialConsent";
import { RulesBox, RULES_MSG } from "@/components/RulesBox";
import { ArrowLeft, Camera, X, Plus, Trash2, Crosshair, Image as ImageIcon, GripVertical } from "lucide-react";
import api, { errMsg, API } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LocationPicker } from "@/components/LocationPicker";
import { useCredits, Paid, PhotosNote, CreditTotal, creditShort } from "@/lib/credits";
import { CityInput, AddressInput } from "@/components/AddressFields";
import { MenuStylePicker, MenuBookViewer, bookEligible } from "@/components/MenuBook";
import { COUNTRIES, PHONE_CODES, CITIES_BY_COUNTRY, cityCoords, nearCountries } from "@/lib/places";

const SUBS = ["Restorante", "Pizzeri", "Fast Food", "Kafe", "Ëmbëltore", "Furrë Buke"];

const DAYS = ["Hënë", "Martë", "Mërkurë", "Enjte", "Premte", "Shtunë", "Diel"];
const SERVICES = ["Delivery", "Rezervime", "Merr me vete", "Ulëse jashtë", "Parking", "Wi-Fi", "Pa duhan", "Po duhan", "Kafshë", "Pa kafshë", "Karikim EV", "WC", "Qasje", "Kënd fëmijësh"];
const PAYMENTS = [
  { key: "visa", label: "Visa" },
  { key: "mastercard", label: "Mastercard" },
  { key: "klarna", label: "Klarna" },
  { key: "paypal", label: "PayPal" },
  { key: "applepay", label: "Apple Pay" },
  { key: "googlepay", label: "Google Pay" },
  { key: "bitcoin", label: "Bitcoin" },
  { key: "cash", label: "Kesh" },
];

function imgUrl(path) {
  return path;
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return imgUrl(r.data.url);
}

function parsePhone(p) { if (!p) return { code: "+46", num: "" }; const m = p.trim().match(/^(\+\d+)\s*(.*)$/); return m ? { code: m[1], num: m[2].replace(/\s/g, "") } : { code: "+46", num: p }; }

function Card({ icon: Icon, title, children, desc }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mt-3">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 text-emerald-500" />}
        <h2 className="font-bold text-slate-800 text-sm uppercase tracking-wide">{title}</h2>
      </div>
      {desc && <p className="text-xs text-slate-400 mb-3">{desc}</p>}
      <div className={desc ? "" : "mt-3"}>{children}</div>
    </section>
  );
}

const label = "text-sm font-semibold text-slate-700 mb-1 block";

export function UshqimForm({ category, onDone, initial, catKey = "ushqim", countries = COUNTRIES, citiesMap = CITIES_BY_COUNTRY, subs = SUBS, phoneCodes = PHONE_CODES, heading = "Ushqim / Pije", subtitle = "Shto lokalin, restorantin ose biznesin tënd të ushqimit.", rules = false }) {
  const nav = useNavigate();
  const { requestGPS } = useLocation();
  const d0 = initial?.data || {};
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pa, setPa] = useState(!!initial?.proximity_alert);
  const [sa, setSa] = useState(initial?.social_allowed ?? null);
  const [am, setAm] = useState(initial?.allow_messages ?? true);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [logo, setLogo] = useState(d0.logo || "");
  const [logoScale, setLogoScale] = useState(d0.logo_scale || 1);
  const [uploading, setUploading] = useState(false);

  const [images, setImages] = useState(initial?.images || []);
  const cr = useCredits(catKey, initial);
  const [f, setF] = useState({ name: initial?.title || "", subcategory: initial?.subcategory || "", description: initial?.description || "", priceAvg: d0.price_avg || "", country: initial?.country || "", city: initial?.city || "", address: initial?.address || "", postal_code: initial?.postal_code || "", instagram: initial?.instagram || "", facebook: initial?.facebook || initial?.data?.facebook || "", website: initial?.website || "", tiktok: initial?.tiktok || "", notes: d0.notes || "" });
  const [coords, setCoords] = useState(initial?.lat != null ? { lat: initial.lat, lng: initial.lng } : null);
  const [phone, setPhone] = useState(() => {
    const p = parsePhone(initial?.phone);
    return phoneCodes.some((c) => c.dial === p.code) ? p : { ...p, code: phoneCodes[0]?.dial || p.code };
  });
  const [hasWhats, setHasWhats] = useState(initial ? !!initial.whatsapp : true);
  const [hasViber, setHasViber] = useState(initial ? !!initial.viber : false);
  const [services, setServices] = useState(d0.services || []);
  const [payment, setPayment] = useState(d0.payment || []);
  const [noMenu, setNoMenu] = useState(d0.no_menu || false);
  const [menu, setMenu] = useState(d0.menu || []); // [{name, items:[{name,desc,price,image}]}]
  const [menuStyle, setMenuStyle] = useState(d0.menu_style || 1);
  const [bookPrev, setBookPrev] = useState(false);
  const [hours, setHours] = useState(d0.hours_week || DAYS.map((d) => ({ day: d, open: d !== "Diel", from: "09:00", to: "22:00", h24: false })));

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - images.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const url = await uploadImage(file);
        setImages((p) => [...p, url]);
      }
      toast.success("Fotot u ngarkuan");
    } catch (er) { toast.error(errMsg(er)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImg = (i) => setImages((p) => p.filter((_, x) => x !== i));
  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const url = await uploadImage(file); setLogo(url); toast.success("Logoja u ngarkua"); }
    catch (er) { toast.error(errMsg(er)); }
    finally { if (logoRef.current) logoRef.current.value = ""; }
  };
  const makeMain = (i) => setImages((p) => { const a = [...p]; const [m] = a.splice(i, 1); return [m, ...a]; });
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const setHour = (i, patch) => setHours((p) => p.map((h, x) => (x === i ? { ...h, ...patch } : h)));

  const gps = async () => {
    try { const c = await requestGPS(); setCoords({ lat: c.lat, lng: c.lng }); toast.success("Vendndodhja u ruajt"); }
    catch { toast.error("Nuk u mor vendndodhja"); }
  };

  // Menu helpers
  const addMenuCat = () => setMenu((p) => [...p, { name: "Kategori e re", items: [] }]);
  const renameCat = (ci, name) => setMenu((p) => p.map((c, i) => (i === ci ? { ...c, name } : c)));
  const delCat = (ci) => setMenu((p) => p.filter((_, i) => i !== ci));
  const addItem = (ci) => setMenu((p) => p.map((c, i) => (i === ci ? { ...c, items: [...c.items, { name: "", desc: "", price: "", image: "" }] } : c)));
  const setItem = (ci, ii, patch) => setMenu((p) => p.map((c, i) => (i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : c)));
  const delItem = (ci, ii) => setMenu((p) => p.map((c, i) => (i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c)));
  const uploadItemImg = async (ci, ii, file) => {
    try { const url = await uploadImage(file); setItem(ci, ii, { image: url }); }
    catch (er) { toast.error(errMsg(er)); }
  };

  const submit = async () => {
    if (sa === null && cr.shown("social")) { toast.error(SOCIAL_MSG); return; }
    if (creditShort(cr, { logo: !!logo, address: !!f.address })) return toast.error("Nuk keni GjejKredit të mjaftueshme");
    if (!f.name) return toast.error("Shkruaj emrin e lokalit");
    if (!f.subcategory) return toast.error("Zgjidh nënkategorinë");
    if (!f.country || !f.city || !f.address) return toast.error("Plotëso vendndodhjen");
    if (!accepted) return toast.error(RULES_MSG);
    setSaving(true);
    const c = coords || cityCoords(f.country, f.city);
    const payload = {
      category: catKey, subcategory: f.subcategory, title: f.name, description: f.description,
      price: null, price_unit: "", country: f.country, city: f.city, address: f.address, postal_code: f.postal_code || "",
      lat: c?.lat ?? null, lng: c?.lng ?? null, images,
      phone: phone.num ? `${phone.code} ${phone.num}` : "",
      whatsapp: phone.num && hasWhats ? `${phone.code}${phone.num}` : "",
      viber: phone.num && hasViber ? `${phone.code}${phone.num}` : "",
      instagram: f.instagram, facebook: f.facebook, website: f.website, tiktok: f.tiktok, proximity_alert: pa, social_allowed: sa, allow_messages: am,
      data: {
        loc_auto: !coords, price_avg: f.priceAvg, services, payment, notes: f.notes, logo, logo_scale: logoScale,
        hours_week: hours, menu: noMenu ? [] : menu, no_menu: noMenu, menu_style: menuStyle,
        open_now: hours.some((h) => h.open),
      },
    };
    cr.strip(payload);
    try {
      const r = initial ? await api.put(`/listings/${initial.id}`, payload) : await api.post("/listings", payload);
      toast.success(initial ? "U dërgua për aprovim" : "U publikua me sukses!");
      onDone ? onDone(initial ? initial.id : r.data.id) : nav(`/shpallje/${initial ? initial.id : r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="gs-fade px-4 pt-4 pb-10 bg-slate-50">
      <button data-testid="ushqim-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />{initial ? "Anulo" : "Publiko"}</button>
      <h1 className="text-2xl font-extrabold text-slate-900">{initial ? "Ndrysho" : "Publiko"}: {heading}</h1>
      <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>

      {/* 2. FOTOT */}
      <Card icon={Camera} title="Foto të lokalit" desc="Shto deri në 10 foto — fotoja e parë është kryesore.">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickFiles} className="hidden" data-testid="ushqim-file-input" />
        <div className="grid grid-cols-3 gap-2">
          {images.map((src, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group">
              <img src={src} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Kryesore</span>}
              <button onClick={() => removeImg(i)} data-testid={`remove-img-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
              {i !== 0 && <button onClick={() => makeMain(i)} className="absolute bottom-1 left-1 bg-white/90 text-slate-700 text-[9px] font-semibold px-1.5 py-0.5 rounded">Bëj kryesore</button>}
            </div>
          ))}
          {images.length < cr.maxImg && (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ushqim-add-photo" className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 flex flex-col items-center justify-center text-emerald-600 gap-1">
              <Plus className="w-6 h-6" />
              <span className="text-xs font-semibold">{uploading ? "Duke ngarkuar..." : "Shto foto"}</span>
            </button>
          )}
        </div>
        <PhotosNote cr={cr} count={images.length} />
      </Card>

      {/* 3. INFORMACIONI */}
      <Card title="Informacioni">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 rounded-full bg-slate-900 ring-2 ring-slate-100 overflow-hidden flex items-center justify-center shrink-0">
            {logo ? <img src={logo} alt="logo" style={{ transform: `scale(${logoScale})` }} className="w-full h-full object-cover" /> : <ImageIcon className="w-7 h-7 text-slate-400" />}
          </div>
          <div className="flex-1">
            <label className={label}>Logoja e biznesit</label>
            <input ref={logoRef} type="file" accept="image/*" onChange={pickLogo} className="hidden" data-testid="ushqim-logo-input" />
            <button type="button" onClick={() => logoRef.current?.click()} data-testid="ushqim-logo-btn" className="text-sm font-semibold text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2">{logo ? "Ndrysho logon" : "Shto logo"}</button>
            {logo && (
              <div className="mt-2">
                <div className="text-xs text-slate-400 mb-1">Madhësia e logos</div>
                <input type="range" min="0.5" max="2.5" step="0.05" value={logoScale} onChange={(e) => setLogoScale(parseFloat(e.target.value))} className="w-full accent-emerald-500" data-testid="ushqim-logo-scale" />
              </div>
            )}
          </div>
        </div>
        <label className={label}>Emri i lokalit *</label>
        <Input data-testid="ushqim-name" placeholder="p.sh. Restaurant Shqiponja" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" />
        <label className={`${label} mt-3`}>Nënkategoria *</label>
        <select data-testid="ushqim-subcategory" value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none">
          <option value="">Zgjidh...</option>
          {(f.subcategory && !subs.includes(f.subcategory) ? [...subs, f.subcategory] : subs).map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className={`${label} mt-3`}>Përshkrimi</label>
        <Textarea data-testid="ushqim-description" placeholder="Përshkruaj lokalin, ushqimin dhe shërbimin..." value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" />
      </Card>

      {/* 4. ÇMIMI */}
      <Card title="Çmimi">
        <label className={label}>Çmimi mesatar</label>
        <Input data-testid="ushqim-price-avg" placeholder="p.sh. 10–20 €" value={f.priceAvg} onChange={(e) => setF({ ...f, priceAvg: e.target.value })} className="rounded-xl h-11" />
      </Card>

      {/* 5. VENDNDODHJA */}
      <Card title="Vendndodhja">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={label}>Shteti *</label>
            <select data-testid="ushqim-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, city: "" })} className="w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm outline-none">
              <option value="">Zgjidh</option>
              {nearCountries(countries).map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Qyteti *</label>
            <CityInput setCoords={setCoords} testid="ushqim-city" country={f.country} value={f.city} onChange={(v) => setF((x) => ({ ...x, city: v }))} cities={Object.keys(citiesMap[f.country] || {})} className="w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm outline-none disabled:text-slate-400" />
          </div>
        </div>
        <label className={`${label} mt-3`}>Adresa *</label>
        <AddressInput testid="ushqim-address" placeholder="Rruga, numri" f={f} setF={setF} setCoords={setCoords} className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm" />
          <Paid cr={cr} k="map"><LocationPicker coords={coords} setCoords={setCoords} country={f.country} city={f.city} address={f.address} testid="ushqim-loc" /></Paid>
      </Card>

      {/* 6. ORARI */}
      <Paid cr={cr} k="hours"><Card title="Orari i punës">
        <div className="space-y-1.5">
          {hours.map((h, i) => (
            <div key={h.day} className="flex items-center gap-2 py-1">
              <span className="w-16 text-sm font-medium text-slate-700">{h.day}</span>
              <button data-testid={`day-toggle-${i}`} onClick={() => setHour(i, { open: !h.open })} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${h.open ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{h.open ? "Hapur" : "Mbyllur"}</button>
              {h.open && !h.h24 && (
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <input type="time" value={h.from} onChange={(e) => setHour(i, { from: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-lg px-1.5 py-1 text-xs flex-1 min-w-0 outline-none" />
                  <span className="text-slate-300">→</span>
                  <input type="time" value={h.to} onChange={(e) => setHour(i, { to: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-lg px-1.5 py-1 text-xs flex-1 min-w-0 outline-none" />
                </div>
              )}
              {h.open && <button onClick={() => setHour(i, { h24: !h.h24 })} className={`text-xs font-semibold px-2 py-1 rounded-full ml-auto shrink-0 ${h.h24 ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-400"}`}>24h</button>}
            </div>
          ))}
        </div>
      </Card></Paid>

      {/* 7. SHËRBIMET */}
      <Paid cr={cr} k="services"><Card title="Çfarë ofron lokali">
        <div className="grid grid-cols-2 gap-2">
          {SERVICES.map((s) => (
            <button key={s} data-testid={`service-${s}`} onClick={() => toggle(services, setServices, s)} className={`flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl border text-left ${services.includes(s) ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 text-slate-600"}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${services.includes(s) ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>{services.includes(s) && <span className="text-white text-[10px]">✓</span>}</span>
              {s}
            </button>
          ))}
        </div>
      </Card></Paid>

      {/* 8. PAGESA */}
      <Paid cr={cr} k="payment"><Card title="Metodat e pagesës">
        <div className="grid grid-cols-2 gap-2">
          {PAYMENTS.map((o) => (
            <button key={o.key} data-testid={`payment-${o.key}`} onClick={() => toggle(payment, setPayment, o.key)} className={`flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl border text-left ${payment.includes(o.key) ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 text-slate-600"}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${payment.includes(o.key) ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>{payment.includes(o.key) && <span className="text-white text-[10px]">✓</span>}</span>
              {o.label}
            </button>
          ))}
        </div>
      </Card></Paid>

      {/* 9. MENUJA */}
      <Paid cr={cr} k="menu"><Card title={catKey === "reklame" ? "Produktet / Menyja" : "Menuja e lokalit"}>
        <label className="flex items-center gap-2 text-sm text-slate-600 mb-3">
          <input type="checkbox" data-testid="no-menu" checked={noMenu} onChange={(e) => setNoMenu(e.target.checked)} className="w-4 h-4 accent-emerald-500" />
          Nuk kam menu
        </label>
        {!noMenu && (
          <div className="space-y-3">
            {menu.map((c, ci) => (
              <div key={ci} className="bg-slate-50 rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-slate-300" />
                  <input data-testid={`menu-cat-${ci}`} value={c.name} onChange={(e) => renameCat(ci, e.target.value)} className="flex-1 bg-white border border-slate-200 rounded-lg px-2 h-9 text-sm font-bold outline-none" />
                  <button onClick={() => delCat(ci)} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2 mt-2">
                  {c.items.map((it, ii) => (
                    <div key={ii} className="bg-white rounded-lg border border-slate-200 p-2">
                      <div className="flex gap-2">
                        <label className="w-12 h-12 shrink-0 rounded-lg border border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer bg-slate-50">
                          {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <Camera className="w-4 h-4 text-slate-400" />}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadItemImg(ci, ii, e.target.files[0])} />
                        </label>
                        <div className="flex-1 min-w-0 space-y-1">
                          <input data-testid={`item-name-${ci}-${ii}`} value={it.name} onChange={(e) => setItem(ci, ii, { name: e.target.value })} placeholder="Emri (p.sh. Pizza, Xhaketë, Veturë)" className="w-full border border-slate-200 rounded-lg px-2 h-8 text-sm outline-none" />
                          <div className="flex gap-1">
                            <input value={it.desc} onChange={(e) => setItem(ci, ii, { desc: e.target.value })} placeholder="Përshkrim" className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 h-8 text-xs outline-none" />
                            <input data-testid={`item-price-${ci}-${ii}`} value={it.price} onChange={(e) => setItem(ci, ii, { price: e.target.value })} placeholder="Çmimi €" inputMode="decimal" className="w-24 border border-emerald-300 bg-emerald-50/40 rounded-lg px-2 h-8 text-sm font-semibold outline-none" />
                          </div>
                        </div>
                        <button onClick={() => delItem(ci, ii)} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                  <button data-testid={`add-item-${ci}`} onClick={() => addItem(ci)} className="w-full flex items-center justify-center gap-1 text-emerald-600 text-sm font-semibold border border-dashed border-emerald-300 rounded-lg py-2"><Plus className="w-4 h-4" />Shto produkt</button>
                </div>
              </div>
            ))}
            <button data-testid="add-menu-cat" onClick={addMenuCat} className="w-full flex items-center justify-center gap-1 bg-emerald-50 text-emerald-700 font-semibold rounded-xl py-3"><Plus className="w-4 h-4" />Shto kategori</button>
            {bookEligible(catKey, f.subcategory) && (
              <MenuStylePicker value={menuStyle} onChange={setMenuStyle} onPreview={() => setBookPrev(true)} hasItems={menu.some((c) => (c.items || []).some((it) => it.name || it.image))} />
            )}
            {bookPrev && (
              <MenuBookViewer theme={menuStyle} logo={logo} title={f.name} onClose={() => setBookPrev(false)}
                items={menu.flatMap((c) => (c.items || []).filter((it) => it.name || it.image).map((it) => ({ ...it, cat: c.name })))} />
            )}
          </div>
        )}
      </Card></Paid>

      {/* 10. KONTAKT */}
      <Paid cr={cr} k="contact"><Card title="Kontakt & Rrjetet Sociale" desc="Të gjitha opsionale.">
        <div className="space-y-3">
          <div>
            <label className={label}>Telefon</label>
            <div className="flex gap-2">
              <select data-testid="ushqim-phone-code" value={phone.code} onChange={(e) => setPhone({ ...phone, code: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm font-semibold outline-none">
                {phoneCodes.map((c) => <option key={c.dial} value={c.dial}>{c.flag} {c.dial}</option>)}
              </select>
              <Input data-testid="ushqim-phone" inputMode="tel" placeholder="Numri i telefonit" value={phone.num} onChange={(e) => setPhone({ ...phone, num: e.target.value })} className="rounded-xl h-11 flex-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="toggle-whatsapp" onClick={() => setHasWhats(!hasWhats)} className={`flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl border ${hasWhats ? "border-green-400 bg-green-50 text-green-700" : "border-slate-200 text-slate-500"}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${hasWhats ? "bg-green-500 border-green-500" : "border-slate-300"}`}>{hasWhats && <span className="text-white text-[10px]">✓</span>}</span>
              Ky numër ka WhatsApp
            </button>
            <button type="button" data-testid="toggle-viber" onClick={() => setHasViber(!hasViber)} className={`flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl border ${hasViber ? "border-purple-400 bg-purple-50 text-purple-700" : "border-slate-200 text-slate-500"}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${hasViber ? "bg-purple-500 border-purple-500" : "border-slate-300"}`}>{hasViber && <span className="text-white text-[10px]">✓</span>}</span>
              Ky numër ka Viber
            </button>
          </div>
          <div>
            <label className={label}>Instagram</label>
            <Input data-testid="ushqim-instagram" placeholder="username ose link" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} className="rounded-xl h-11" />
          </div>
          <div>
            <label className={label}>Facebook</label>
            <Input data-testid="ushqim-facebook" placeholder="faqja ose link" value={f.facebook} onChange={(e) => setF({ ...f, facebook: e.target.value })} className="rounded-xl h-11" />
          </div>
          <div>
            <label className={label}>Web faqja e biznesit</label>
            <Input data-testid="ushqim-website" placeholder="www.biznesi.com" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="rounded-xl h-11" />
          </div>
          <div>
            <label className={label}>TikTok</label>
            <Input data-testid="ushqim-tiktok" placeholder="username ose link" value={f.tiktok} onChange={(e) => setF({ ...f, tiktok: e.target.value })} className="rounded-xl h-11" />
          </div>
        </div>
      </Card></Paid>

      {/* 11. KOMENTE */}
      <Card title="Komente">
        <Textarea data-testid="ushqim-notes" placeholder="Shkruaj ndonjë informacion shtesë..." value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl" />
      </Card>

      {/* 12. PUBLIKO */}
      <Paid cr={cr} k="proximity"><div className="mt-4"><ProximityToggle value={pa} onChange={setPa} /></div></Paid>
      <Paid cr={cr} k="social"><SocialConsent value={sa} onChange={setSa} /></Paid>
      <Paid cr={cr} k="messages"><MessagesConsent value={am} onChange={setAm} /></Paid>
      <RulesBox catKey={catKey} accepted={accepted} onAccept={setAccepted} />
      <CreditTotal cr={cr} extra={{ logo: !!logo, address: !!f.address }} />
      <Button data-testid="ushqim-submit" onClick={submit} disabled={saving || !accepted || creditShort(cr, { logo: !!logo, address: !!f.address })} className="w-full h-13 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 font-bold text-base mt-4 shadow-lg shadow-emerald-500/30">
        {saving ? "Duke publikuar..." : "Publiko"}
      </Button>
    </div>
  );
}

export default UshqimForm;
